import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { closeExpandableColumn } from '../../redux/Slices/expandableColumnSlice';
import useNavigationGuard from '../../hooks/useNavigationGuard';
import useHandlePopoverClick from '../../hooks/useHandlePopoverClick';
import useHandleExpandableColumnClick from '../../hooks/useHandleExpandableColumnClick';

// Icons
import WorkspacesIcon from '../icons/Workspaces';
import SearchIcon from '../icons/Search';
import NotificationsIcon from '../icons/Notifications';
import CloseIcon from '../icons/Close';
import NewLabelIcon from '../icons/NewLabel';
import AddIcon from '../icons/Add';
import TextSnippetIcon from '../icons/TextSnippet';
import CampaignIcon from '../icons/Campaign';
import MemberOnlineStatus from '../Global/MemberOnlineStatus';
import ImagePlaceholder from '../Global/ImagePlaceholder';

import './MobileBottomNav.scss';
import KeyboardArrowUp from '../icons/KeyboardArrowUp';
import { config } from '../../config';

const MobileBottomNav = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [newNotification, setNewNotification] = useState(0);
  const { user } = useSelector((state) => state.user);
  const { board } = useSelector((state) => state.board);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { guardedNavigate, canNavigate } = useNavigationGuard();
  const { handlePopoverClick } = useHandlePopoverClick();
  const { handleExpandableColumnClick, expandableColumnState } = useHandleExpandableColumnClick();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  // Close menu and expandable column when location changes (mobile only)
  useEffect(() => {
    closeMenu();
    if (window.innerWidth <= 768) {
      dispatch(closeExpandableColumn());
    }
  }, [location.pathname]);

  // Listen for notification count updates from Sidebar (avoids duplicate fetch)
  useEffect(() => {
    const handleCountUpdate = (e) => {
      setNewNotification(e.detail.count);
    };
    window.addEventListener('notificationCountUpdated', handleCountUpdate);
    return () => window.removeEventListener('notificationCountUpdated', handleCountUpdate);
  }, []);

  const addNewNotificationsCount = async (count) => {
    try {
      const response = await fetch(`${config.API_URI}/v1/notifications/count`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ newNotificationsCount: count }),
      });

      if (!response.ok) throw new Error('Failed to add new notification count');
      await response.json();
    } catch (error) {
      console.error('Error adding notification count:', error);
    }
  };

  const handleNavClick = (path, action) => {
    closeMenu();
    dispatch(closeExpandableColumn()); // Always close column first
    if (action) {
      action();
    } else if (path && canNavigate(path)) {
      guardedNavigate(path);
    }
  };

  const handleGridItemClick = (contentId, props = {}) => {
    closeMenu();
    // Special case for swapping columns
    if (expandableColumnState.isVisible && expandableColumnState.contentId !== contentId) {
      dispatch(closeExpandableColumn());
      setTimeout(() => {
        if (contentId === 'notifications') {
          setNewNotification(0);
          addNewNotificationsCount(0);
        }
        handleExpandableColumnClick(contentId, props);
      }, 100);
    } else {
      if (contentId === 'notifications') {
        setNewNotification(0);
        addNewNotificationsCount(0);
      }
      handleExpandableColumnClick(contentId, props);
    }
  };

  const isPathActive = (path) => {
    // Exact match for the path, and ensure NO column is currently open
    return location.pathname === path && !expandableColumnState.isVisible;
  };
  
  const isExpandableActive = (id) => expandableColumnState.isVisible && expandableColumnState.contentId === id;

  const navItems = [
    {
      id: 'workspaces',
      label: 'Workspaces',
      path: `/u/${user?.user?.username}/boards`,
      icon: (active) => <WorkspacesIcon size={20} color={active ? "var(--brand-color)" : "var(--primary-text-color)"} />,
      onClick: () => {
        const targetPath = `/u/${user?.user?.username}/boards`;
        closeMenu();
        
        if (location.pathname === targetPath) {
          // If already on path, toggle the column
          handleGridItemClick('navWorkspaces');
        } else if (canNavigate(targetPath)) {
          // If not on path, close any open column and navigate
          dispatch(closeExpandableColumn());
          guardedNavigate(targetPath);
        }
      }
    },
    {
      id: 'search',
      label: 'Search',
      icon: (active) => <SearchIcon size={20} color={active ? "var(--brand-color)" : "var(--primary-text-color)"} />,
      onClick: () => {
        closeMenu();
        dispatch(closeExpandableColumn()); // Close column when searching
        window.dispatchEvent(new CustomEvent('toggleSearch'));
      }
    },
    {
      id: 'create',
      label: 'Create',
      icon: () => <div className="minimal-create-icon"><AddIcon size={32} /></div>,
      onClick: () => {
        closeMenu();
        dispatch(closeExpandableColumn()); // Close column when creating
        window.dispatchEvent(new CustomEvent('openCreateWorkspace'));
      }
    },
    {
      id: 'notifications',
      label: 'Activity',
      icon: (active) => (
        <div className="icon-with-badge">
          <NotificationsIcon size={20} />
          {newNotification > 0 && <span className="notification-counter">{newNotification}</span>}
        </div>
      ),
      onClick: () => handleGridItemClick('notifications')
    },
    {
      id: 'profile',
      label: 'Profile',
      icon: () => (
        <div className="nav-profile-avatar minimal">
          {user?.user?.profilePicture ? (
            <img src={user?.user?.profilePicture} alt={user?.user?.username} />
          ) : (
            <ImagePlaceholder
              member={user?.user}
              text={user?.user?.username || user?.user?.name}
              size={28}
            />
          )}
        </div>
      ),
      onClick: (e) => {
        closeMenu();
        dispatch(closeExpandableColumn()); // Close column when opening profile
        handlePopoverClick(e, 'profile', { userInfo: user });
      }
    }
  ];

  const gridMenuItems = [
    {
      id: 'workspaces',
      label: 'Boards',
      path: `/u/${user?.user?.username}/boards`,
      icon: (active) => <WorkspacesIcon size={24} color={active ? "var(--brand-color)" : "var(--brand-color)"} />,
      onClick: () => {
        const targetPath = `/u/${user?.user?.username}/boards`;
        if (canNavigate(targetPath)) {
          dispatch(closeExpandableColumn());
          guardedNavigate(targetPath);
          closeMenu();
        }
      }
    },
    {
      id: 'search',
      label: 'Search',
      icon: () => <SearchIcon size={24} />,
      onClick: () => {
        closeMenu();
        dispatch(closeExpandableColumn());
        window.dispatchEvent(new CustomEvent('toggleSearch'));
      }
    },
    {
      id: 'create',
      label: 'Create',
      icon: () => <AddIcon size={24} />,
      onClick: () => {
        closeMenu();
        dispatch(closeExpandableColumn());
        window.dispatchEvent(new CustomEvent('openCreateWorkspace'));
      }
    },
    {
      id: 'notifications',
      label: 'Activity',
      icon: (active) => (
        <div className="icon-with-badge">
          <NotificationsIcon size={24} />
          {newNotification > 0 && <span className="notification-counter">{newNotification}</span>}
        </div>
      ),
      onClick: () => {
        closeMenu();
        handleGridItemClick('notifications');
      }
    },
    {
      id: 'white-label',
      label: 'White Label',
      path: `/u/${user?.user?.username}/white-label`,
      icon: () => <NewLabelIcon size={24} />,
      onClick: () => handleNavClick(`/u/${user?.user?.username}/white-label`)
    },
    {
      id: 'navRecent',
      label: 'Recent',
      icon: () => (
        <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
          <path d="M480-80q-155 0-269-103T82-440h81q15 121 105.5 200.5T480-160q134 0 227-93t93-227q0-134-93-227t-227-93q-86 0-159.5 42.5T204-640h116v80H88q29-140 139-230t253-90q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm112-232L440-464v-216h80v184l128 128-56 56Z" />
        </svg>
      ),
      onClick: () => {
        handleGridItemClick('navRecent', { boardId: board?.shortId })
      }
    },
    {
      id: 'navStarred',
      label: 'Starred',
      icon: () => (
        <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
          <path d="m363-310 117-71 117 71-31-133 104-90-137-11-53-126-53 126-137 11 104 90-31 133ZM480-28 346-160H160v-186L28-480l132-134v-186h186l134-132 134 132h186v186l132 134-132 134v186H614L480-28Zm0-112 100-100h140v-140l100-100-100-100v-140H580L480-820 380-720H240v140L140-480l100 100v140h140l100 100Zm0-340Z" />
        </svg>
      ),
      onClick: () => {
        handleGridItemClick('navStarred', { boardId: board?.shortId })
      }
    },
    {
      id: 'navPages',
      label: 'Pages',
      icon: () => <TextSnippetIcon size={24} />,
      onClick: () => {
        handleGridItemClick('navPages')
      }
    },
    {
      id: 'feedBack',
      label: 'Feedback',
      icon: () => <CampaignIcon size={24} />,
      onClick: () => {
        handleGridItemClick('feedBack')
      }
    },
    {
      id: 'profile-settings',
      label: 'Settings',
      path: `/u/${user?.user?.username}/profile`,
      icon: () => (
        <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="currentColor">
          <path d="m370-80-16-128q-13-5-24.5-12T307-235l-119 50L78-375l103-78q-1-7-1-13.5t1-13.5l-103-78 110-190 119 50q11-8 23-15t24-12l16-128h220l16 128q13 5 24.5 12t22.5 15l119-50 110 190-103 78q1 7 1 13.5t-1 13.5l103 78-110 190-119-50q-11 8-23 15t-24 12L590-80H370Zm112-260q58 0 99-41t41-99q0-58-41-99t-99-41q-58 0-99 41t-41 99q0 58 41 99t99 41Zm0-80q-25 0-42.5-17.5T422-480q0-25 17.5-42.5T480-540q25 0 42.5 17.5T540-480q0 25-17.5 42.5T480-420Z" />
        </svg>
      ),
      onClick: () => handleNavClick(`/u/${user?.user?.username}/profile`)
    }
  ];


  return (
    <div className={`mobile-bottom-nav-container ${isMenuOpen ? 'menu-open' : ''}`}>
      {/* Subtle Slide Handle Trigger */}
      <div className="mobile-nav-handle-minimal" onClick={toggleMenu}>
        <KeyboardArrowUp size={24} color="#ddd" />
      </div>

      {/* Minimal Full Menu Overlay */}
      <div className={`mobile-full-menu-overlay ${isMenuOpen ? 'open' : ''}`} onClick={closeMenu}>
        <div className="mobile-full-menu-content minimal" onClick={(e) => e.stopPropagation()}>
          <div className="menu-header-minimal">
            <h3 className="menu-title-minimal">Menu</h3>
            <button className="close-btn-minimal" onClick={closeMenu}>
              <CloseIcon size={20} />
            </button>
          </div>

          <div className="nav-grid-minimal">
            {gridMenuItems.map((item, index) => {
              const isActive = (item.path && isPathActive(item.path)) || (item.id && isExpandableActive(item.id));
              return (
                <div key={index} className={`nav-item-minimal ${isActive ? 'active' : ''}`} onClick={item.onClick}>
                  <div className="icon-minimal">{item.icon(isActive)}</div>
                  <div className="label-minimal">{item.label}</div>
                </div>
              );
            })}
          </div>

        </div>
      </div>

      {/* Main Bottom Navbar Minimal */}
      <nav className="mobile-bottom-nav minimal">
        {navItems.map((item) => {
          const isActive = (item.id && isExpandableActive(item.id)) || 
                          (item.id === 'workspaces' && isExpandableActive('navWorkspaces')) ||
                          (!expandableColumnState.isVisible && item.path && isPathActive(item.path));
          
          return (
            <div
              key={item.id}
              id={`${item.id}-btn`}
              className={`nav-item-main ${item.id === 'create' ? 'center-create' : ''} ${isActive ? 'active' : ''}`}
              onClick={(e) => item.onClick ? item.onClick(e) : handleNavClick(item.path)}
              data-popover-trigger
            >
              <div className="icon-wrapper">
                {item.icon(isActive)}
              </div>
              {(item.id !== 'create' && item.id !== 'profile') && <span className="label-main">{item.label}</span>}
            </div>
          );
        })}
      </nav>
    </div>
  );
};

export default MobileBottomNav;
